// O que está guardado no aparelho: as linhas de cada tabela e a fila de
// alterações ainda não enviadas. Toda gravação feita no aparelho entra nas duas
// coisas numa transação só, para nunca ficar uma sem a outra.
//
// Fila (cada item):
//   { seq, empresa_id, tabela, tipo: 'criar' | 'alterar', registro_id, dados,
//     estado: 'pendente' | 'enviando' | 'recusada', erro, tentado, criada_em }
//   - criar: dados = a linha inteira;  alterar: dados = só os campos mudados
//     (excluir é alterar com deleted_at).
//   - Alterações seguidas no mesmo registro viram um item só, desde que o item
//     anterior ainda não esteja indo para o servidor e seja o último da fila —
//     assim a ordem entre registros diferentes nunca muda.
//   - "tentado": o item já saiu para o servidor alguma vez. Uma criação tentada
//     pode ter chegado lá (e a resposta se perdido); reenviá-la não grava nada
//     por cima. Por isso nada mais entra nela: o que vier depois vai num item
//     de alteração, e excluir vira exclusão de verdade.
//   - Item recusado pelo servidor segura os seguintes do mesmo registro. Quem
//     corrige o registro corrige o próprio item recusado (que absorve os
//     seguintes do mesmo registro) e ele volta a pendente.
//   - Item que aponta para um registro cuja criação ainda está na fila
//     (carreta → caminhão, remuneração → colaborador ou caminhão) espera essa
//     criação chegar ao servidor.
//   - Registro criado e excluído antes de chegar ao servidor simplesmente some
//     (se nada na fila aponta para ele).
//
// A tabela "empresas" fica guardada dentro dos vínculos (uma por conta).
import { TABELAS, bancoAberto } from './banco.js';

const CAMPOS_DO_SERVIDOR = ['created_at', 'updated_at', 'created_by'];

// Campos que apontam para outro registro guardado no aparelho. Cada tabela
// nova que entrar no aparelho (viagens, fretes…) precisa estar aqui.
const REFERENCIAS = {
  veiculos: { acoplado_a: 'veiculos' },
  regras_remuneracao: { colaborador_id: 'colaboradores', veiculo_id: 'veiculos' },
  contas_fixas: { veiculo_id: 'veiculos', categoria_id: 'categorias' },
  viagens: { veiculo_id: 'veiculos', motorista_id: 'colaboradores' },
  fretes: { viagem_id: 'viagens', cliente_id: 'clientes', agenciador_id: 'colaboradores', complemento_de: 'fretes' },
  entregas: { frete_id: 'fretes' },
  lancamentos: {
    veiculo_id: 'veiculos', colaborador_id: 'colaboradores', viagem_id: 'viagens', frete_id: 'fretes',
    categoria_id: 'categorias', recorrencia_id: 'contas_fixas',
  },
  parcelas: { lancamento_id: 'lancamentos' },
  anexos: { lancamento_id: 'lancamentos', frete_id: 'fretes' },
  acertos: { colaborador_id: 'colaboradores' },
  manutencao_itens: { veiculo_id: 'veiculos' },
  viagem_eventos: { viagem_id: 'viagens', frete_id: 'fretes' },
};

// Tabelas com linhas do sistema (sem empresa), que valem para todas.
const COM_LINHAS_DO_SISTEMA = new Set(['categorias']);

// Colunas que o banco calcula ou carimba: o aparelho pode guardar uma cópia
// para mostrar, mas nunca as envia.
const CALCULADAS = {
  // acerto_id: só o fechamento do acerto grava (a carga entra pela data).
  // campos_do_motorista: o banco anota o que o motorista preencheu.
  fretes: ['valor_liquido', 'saldo_valor', 'acerto_id', 'campos_do_motorista'],
  lancamentos: ['assinatura_pedida_em'],
  acertos: ['assinatura_pedida_em'],
  // A última vez feita pelas despesas: o banco anota (o plano tem a base).
  manutencao_itens: ['feita_data', 'feita_km'],
};

export function semCalculadas(tabela, dados) {
  const campos = CALCULADAS[tabela];
  if (!campos) return dados;
  const copia = { ...dados };
  for (const campo of campos) delete copia[campo];
  return copia;
}

const chaveDe = (tabela, id) => `${tabela}:${id}`;

// Registros para os quais o item aponta ("tabela:id").
function referenciasDe(item) {
  const campos = REFERENCIAS[item.tabela];
  if (!campos || !item.dados) return [];
  return Object.entries(campos)
    .filter(([campo]) => item.dados[campo])
    .map(([campo, tabela]) => chaveDe(tabela, item.dados[campo]));
}

// O que cada item da fila espera para poder ir: Map(seq → null | { tabela,
// registro_id, motivo }). Um item espera o anterior do mesmo registro (a
// ordem de um registro nunca muda) e a criação, ainda na fila, do registro
// para o qual ele aponta. `itens` é a fila inteira, na ordem.
export function esperas(itens) {
  const criacoes = new Map();
  for (const item of itens) if (item.tipo === 'criar') criacoes.set(chaveDe(item.tabela, item.registro_id), item);
  const primeiros = new Map();
  const resultado = new Map();
  for (const item of itens) {
    const propria = chaveDe(item.tabela, item.registro_id);
    const anterior = primeiros.get(propria);
    let espera = null;
    if (anterior) {
      espera = { tabela: anterior.tabela, registro_id: anterior.registro_id, motivo: 'mesmo-registro' };
    } else {
      primeiros.set(propria, item);
      for (const alvo of referenciasDe(item)) {
        const criacao = alvo !== propria ? criacoes.get(alvo) : null;
        if (criacao) {
          espera = { tabela: criacao.tabela, registro_id: criacao.registro_id, motivo: 'referencia' };
          break;
        }
      }
    }
    resultado.set(item.seq, espera);
  }
  return resultado;
}

// Campos em que `dados` difere de `atual` (o formulário manda todos; na fila
// só vai o que mudou, para não desfazer o que outro aparelho mudou nos demais).
export function camposMudados(atual, dados) {
  const mudados = {};
  for (const [campo, valor] of Object.entries(dados)) {
    const antes = atual?.[campo];
    const igual = valor !== null && typeof valor === 'object' ? JSON.stringify(valor) === JSON.stringify(antes) : (antes ?? null) === (valor ?? null);
    if (!igual) mudados[campo] = valor;
  }
  return mudados;
}

function lojas(tabela, { comArquivo = false } = {}) {
  const base = tabela === 'empresas' ? ['vinculos', 'fila'] : [tabela, 'fila'];
  return comArquivo ? [...base, 'arquivos'] : base;
}

async function lerLinha(tx, tabela, id) {
  if (tabela === 'empresas') return (await tx.objectStore('vinculos').get(id))?.empresa ?? null;
  return (await tx.objectStore(tabela).get(id)) ?? null;
}

async function gravarLinha(tx, tabela, linha) {
  if (tabela === 'empresas') {
    const vinculo = await tx.objectStore('vinculos').get(linha.id);
    if (vinculo) await tx.objectStore('vinculos').put({ ...vinculo, empresa: linha });
    return;
  }
  await tx.objectStore(tabela).put(linha);
}

async function apagarLinha(tx, tabela, id) {
  if (tabela !== 'empresas') await tx.objectStore(tabela).delete(id);
}

// O que vai para o servidor numa criação: a linha sem as datas e o autor, que
// o banco preenche.
export function paraEnvio(linha, tabela = null) {
  // Sempre uma cópia: a linha continua no aparelho com as datas.
  const copia = { ...semCalculadas(tabela, linha) };
  for (const campo of CAMPOS_DO_SERVIDOR) delete copia[campo];
  if (copia.deleted_at == null) delete copia.deleted_at;
  return copia;
}

// ---------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------
export async function todas(tabela, empresaId) {
  return bancoAberto().getAllFromIndex(tabela, 'empresa_id', empresaId);
}

export async function uma(tabela, id) {
  return (await bancoAberto().get(tabela, id)) ?? null;
}

// Categorias da empresa e as do sistema (que não têm empresa).
export async function categorias(empresaId) {
  return (await bancoAberto().getAll('categorias')).filter((c) => c.empresa_id === null || c.empresa_id === undefined || c.empresa_id === empresaId);
}

export async function filaCompleta() {
  return bancoAberto().getAll('fila');
}

// Resumo para os avisos: quantos itens esperam envio e quantos foram recusados.
export async function resumoDaFila() {
  const itens = await filaCompleta();
  let pendentes = 0;
  let recusadas = 0;
  for (const item of itens) {
    if (item.estado === 'recusada') recusadas += 1;
    else pendentes += 1;
  }
  return { pendentes, recusadas, total: itens.length };
}

// Situação de cada registro de uma tabela que ainda tem algo na fila:
// Map(id → 'pendente' | 'recusada').
export async function situacoes(tabela) {
  const mapa = new Map();
  for (const item of await filaCompleta()) {
    if (item.tabela !== tabela) continue;
    if (item.estado === 'recusada') mapa.set(item.registro_id, 'recusada');
    else if (!mapa.has(item.registro_id)) mapa.set(item.registro_id, 'pendente');
  }
  return mapa;
}

// ---------------------------------------------------------------------
// Gravação feita no aparelho
// ---------------------------------------------------------------------
// Grava a linha e põe (ou junta) o item na fila. Devolve a linha gravada, ou
// null quando o registro sumiu (criado e excluído antes de ir para o servidor).
// `arquivo` (anexo com foto): guarda o arquivo junto, na mesma transação.
export async function gravar(tabela, linha, { tipo, dados, empresaId, arquivo = null }) {
  const db = bancoAberto();
  const tx = db.transaction(lojas(tabela, { comArquivo: Boolean(arquivo) || tabela === 'anexos' }), 'readwrite');
  if (arquivo) {
    await tx.objectStore('arquivos').put({ ...arquivo, id: linha.id, empresa_id: empresaId, enviado: false, guardado_em: new Date().toISOString() });
  }
  const fila = tx.objectStore('fila');
  const doRegistro = await fila.index('registro').getAll([tabela, linha.id]);
  const algumEnviando = doRegistro.some((item) => item.estado === 'enviando');
  const criacao = doRegistro.find((item) => item.tipo === 'criar');
  let resultado = linha;

  // Criado e excluído antes de chegar ao servidor: não há o que enviar. Só
  // vale se a criação nunca saiu (ou saiu e foi recusada, então não está lá)
  // e se nada na fila aponta para o registro (senão, ele é criado e excluído
  // de verdade, e quem aponta para ele continua valendo).
  let podeSumir = tipo === 'alterar' && dados.deleted_at && !algumEnviando && criacao &&
    (!criacao.tentado || criacao.estado === 'recusada');
  if (podeSumir) {
    const alvo = chaveDe(tabela, linha.id);
    podeSumir = !(await fila.getAll()).some((item) => referenciasDe(item).includes(alvo));
  }

  if (podeSumir) {
    for (const item of doRegistro) await fila.delete(item.seq);
    await apagarLinha(tx, tabela, linha.id);
    // A foto que nunca subiu sai junto do aparelho.
    if (tabela === 'anexos') await tx.objectStore('arquivos').delete(linha.id);
    resultado = null;
  } else {
    let juntou = false;
    const recusado = doRegistro.find((item) => item.estado === 'recusada');
    if (tipo === 'alterar' && !algumEnviando && recusado) {
      // Correção de um registro recusado: o que esperava atrás dele e a
      // alteração nova entram no próprio item recusado, que volta a pendente
      // (senão ele seria reenviado com o mesmo problema).
      let juntos = recusado.dados;
      for (const item of doRegistro) {
        if (item.seq <= recusado.seq) continue;
        juntos = { ...juntos, ...item.dados };
        await fila.delete(item.seq);
      }
      await fila.put({ ...recusado, dados: { ...juntos, ...semCalculadas(tabela, dados) }, estado: 'pendente', erro: null });
      juntou = true;
    } else if (tipo === 'alterar' && doRegistro.at(-1)?.estado === 'pendente') {
      const ultima = doRegistro.at(-1);
      // A última da fila pela lista de chaves (sem cursor: veja esquecerEmpresas).
      const ultimaSeq = (await fila.getAllKeys()).at(-1);
      if (ultimaSeq === ultima.seq && !(ultima.tipo === 'criar' && ultima.tentado)) {
        await fila.put({ ...ultima, dados: { ...ultima.dados, ...semCalculadas(tabela, dados) } });
        juntou = true;
      }
    }
    if (!juntou) {
      await fila.add({
        empresa_id: empresaId,
        tabela,
        tipo,
        registro_id: linha.id,
        dados: tipo === 'criar' ? paraEnvio(linha, tabela) : semCalculadas(tabela, dados),
        estado: 'pendente',
        erro: null,
        criada_em: new Date().toISOString(),
      });
    }
    await gravarLinha(tx, tabela, linha);
  }
  await tx.done;
  return resultado;
}

// Linha como está no aparelho agora (a empresa fica dentro do vínculo).
export async function linhaAtual(tabela, id) {
  if (tabela === 'empresas') return (await bancoAberto().get('vinculos', id))?.empresa ?? null;
  return uma(tabela, id);
}

// Item recusado de um registro (ou null).
export async function recusaDoRegistro(tabela, registroId) {
  const itens = await bancoAberto().getAllFromIndex('fila', 'registro', [tabela, registroId]);
  return itens.find((item) => item.estado === 'recusada') ?? null;
}

// Desfaz uma gravação que o servidor recusou na hora (com sinal): tira da
// fila o item recusado e volta a linha para como estava — com o que outra
// gravação feita nesse meio-tempo ainda vai enviar por cima. Se era um
// cadastro novo, ele some (nunca existiu no servidor).
export async function desfazer(tabela, registroId, anterior) {
  const db = bancoAberto();
  const tx = db.transaction(lojas(tabela), 'readwrite');
  const fila = tx.objectStore('fila');
  const restantes = [];
  for (const item of await fila.index('registro').getAll([tabela, registroId])) {
    if (item.estado === 'enviando') continue;
    if (!anterior || item.estado === 'recusada') await fila.delete(item.seq);
    else restantes.push(item);
  }
  if (anterior) {
    let linha = anterior;
    for (const item of restantes) linha = { ...linha, ...item.dados };
    await gravarLinha(tx, tabela, linha);
  } else {
    await apagarLinha(tx, tabela, registroId);
  }
  await tx.done;
}

// ---------------------------------------------------------------------
// Uso pela sincronia
// ---------------------------------------------------------------------
// Pega o próximo item que pode ir (na ordem da fila, fora os que esperam
// outro) e já o marca "enviando", numa transação só: assim nenhuma alteração
// entra nele entre ser lido e ser enviado. Devolve o item como foi marcado.
export async function pegarParaEnviar() {
  const tx = bancoAberto().transaction('fila', 'readwrite');
  const itens = await tx.store.getAll();
  const espera = esperas(itens);
  const item = itens.find((i) => i.estado === 'pendente' && !espera.get(i.seq));
  let marcado = null;
  if (item) {
    marcado = { ...item, estado: 'enviando', tentado: true };
    await tx.store.put(marcado);
  }
  await tx.done;
  return marcado;
}

export async function marcar(seq, estado, erro = null) {
  const db = bancoAberto();
  const tx = db.transaction('fila', 'readwrite');
  const item = await tx.store.get(seq);
  if (item) await tx.store.put({ ...item, estado, erro });
  await tx.done;
}

// Item que ficou "enviando" porque o app fechou no meio: volta a pendente
// (reenviar não duplica: criar ignora id repetido e alterar é o mesmo pedido).
export async function destravarEnvios() {
  const db = bancoAberto();
  const tx = db.transaction('fila', 'readwrite');
  for (const item of await tx.store.getAll()) {
    if (item.estado === 'enviando') await tx.store.put({ ...item, estado: 'pendente' });
  }
  await tx.done;
}

// O servidor aceitou o item: sai da fila e a linha passa a ser a do servidor,
// com as alterações que ainda estão na fila por cima.
export async function confirmarEnvio(item, linhaDoServidor) {
  const db = bancoAberto();
  const tx = db.transaction(lojas(item.tabela), 'readwrite');
  const fila = tx.objectStore('fila');
  await fila.delete(item.seq);
  if (linhaDoServidor) {
    const restantes = await fila.index('registro').getAll([item.tabela, item.registro_id]);
    let linha = linhaDoServidor;
    for (const outro of restantes) linha = { ...linha, ...outro.dados };
    await gravarLinha(tx, item.tabela, linha);
  }
  await tx.done;
}

// Recusados porque apontavam para um registro que ainda não existia no
// servidor (23503) voltam a pendente. Devolve true se algum voltou.
export async function reabrirDependentes() {
  const db = bancoAberto();
  const tx = db.transaction('fila', 'readwrite');
  let algum = false;
  for (const item of await tx.store.getAll()) {
    if (item.estado === 'recusada' && item.erro?.codigo === '23503') {
      await tx.store.put({ ...item, estado: 'pendente', erro: null });
      algum = true;
    }
  }
  await tx.done;
  return algum;
}

export async function tentarDeNovo() {
  const db = bancoAberto();
  const tx = db.transaction('fila', 'readwrite');
  for (const item of await tx.store.getAll()) {
    if (item.estado === 'recusada') await tx.store.put({ ...item, estado: 'pendente', erro: null });
  }
  await tx.done;
}

// Desiste de tudo o que não foi enviado de um registro. Se ele nem chegou a
// existir no servidor, some do aparelho; se já existia, a próxima baixa traz
// de volta a versão do servidor. Devolve true se a linha foi apagada.
// Se o registro nunca chegou ao servidor, o que dependia dele na fila (a
// carga da viagem descartada, a foto da despesa descartada…) vai junto.
export async function descartarRegistro(tabela, registroId) {
  const db = bancoAberto();
  const tx = db.transaction([...TABELAS, 'vinculos', 'fila', 'arquivos'], 'readwrite');
  const fila = tx.objectStore('fila');
  const itens = await fila.getAll();
  const raiz = chaveDe(tabela, registroId);
  const doRegistro = itens.filter((item) => chaveDe(item.tabela, item.registro_id) === raiz);
  if (doRegistro.some((item) => item.estado === 'enviando')) {
    await tx.done;
    throw new Error('ENVIANDO: essa alteração está sendo enviada agora. Espere um instante e tente de novo.');
  }
  const nunca = doRegistro.some((item) => item.tipo === 'criar');
  const sumidos = new Set(nunca ? [raiz] : []);
  const descartados = new Set(doRegistro.map((item) => item.seq));
  // Em cascata: quem aponta para um registro que sumiu também sai.
  for (let achou = nunca; achou;) {
    achou = false;
    for (const item of itens) {
      if (descartados.has(item.seq) || item.estado === 'enviando') continue;
      if (!referenciasDe(item).some((alvo) => sumidos.has(alvo))) continue;
      descartados.add(item.seq);
      achou = true;
      if (item.tipo === 'criar') sumidos.add(chaveDe(item.tabela, item.registro_id));
    }
  }
  for (const seq of descartados) await fila.delete(seq);
  for (const chave of sumidos) {
    const [t, id] = [chave.slice(0, chave.indexOf(':')), chave.slice(chave.indexOf(':') + 1)];
    await apagarLinha(tx, t, id);
    if (t === 'anexos') await tx.objectStore('arquivos').delete(id);
  }
  await tx.done;
  return nunca;
}

// Junta o que veio do servidor com o que está no aparelho. Registro com algo
// na fila fica como está no aparelho (a alteração ainda vai subir); o resto
// passa a ser igual ao servidor, e o que não existe mais lá sai daqui.
// Devolve quantos registros mudaram no aparelho.
export async function mesclar(tabela, empresaId, linhas) {
  const db = bancoAberto();
  const tx = db.transaction([tabela, 'fila'], 'readwrite');
  const loja = tx.objectStore(tabela);
  const naFila = new Set(
    (await tx.objectStore('fila').getAll()).filter((item) => item.tabela === tabela).map((item) => item.registro_id),
  );
  const daEmpresa = COM_LINHAS_DO_SISTEMA.has(tabela)
    ? (await loja.getAll()).filter((l) => l.empresa_id == null || l.empresa_id === empresaId)
    : await loja.index('empresa_id').getAll(empresaId);
  const guardadas = new Map(daEmpresa.map((l) => [l.id, l]));
  let mudou = 0;
  const vindos = new Set();
  for (const linha of linhas) {
    vindos.add(linha.id);
    if (naFila.has(linha.id)) continue;
    const antes = guardadas.get(linha.id);
    if (antes && mesmaLinha(antes, linha)) continue;
    await loja.put(linha);
    mudou += 1;
  }
  for (const id of guardadas.keys()) {
    if (!vindos.has(id) && !naFila.has(id)) {
      await loja.delete(id);
      mudou += 1;
    }
  }
  await tx.done;
  return mudou;
}

function mesmaLinha(a, b) {
  const chaves = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of chaves) {
    if (JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------
// Arquivos das fotos (e PDFs) dos anexos
// ---------------------------------------------------------------------
export async function arquivo(id) {
  return (await bancoAberto().get('arquivos', id)) ?? null;
}

export async function marcarArquivoEnviado(id) {
  const tx = bancoAberto().transaction('arquivos', 'readwrite');
  const registro = await tx.store.get(id);
  if (registro) await tx.store.put({ ...registro, enviado: true, enviado_em: new Date().toISOString() });
  await tx.done;
}

// A cópia no aparelho das fotos que já subiram há mais de `dias` sai (para
// não encher o celular); com sinal, elas continuam abrindo do servidor.
// As que não subiram nunca saem. Devolve quantas saíram.
export async function limparArquivosEnviados(dias = 60, agora = Date.now()) {
  const limite = new Date(agora - dias * 86400000).toISOString();
  const tx = bancoAberto().transaction('arquivos', 'readwrite');
  let saiu = 0;
  for (const registro of await tx.store.getAll()) {
    const quando = registro.enviado_em ?? registro.guardado_em ?? '';
    if (registro.enviado && quando < limite) {
      await tx.store.delete(registro.id);
      saiu += 1;
    }
  }
  await tx.done;
  return saiu;
}

// ---------------------------------------------------------------------
// Anotações só deste aparelho (não vão para o servidor)
// ---------------------------------------------------------------------
export async function lerLocal(chave) {
  return (await bancoAberto().get('meta', `local:${chave}`))?.valor ?? null;
}

export async function gravarLocal(chave, valor) {
  await bancoAberto().put('meta', { chave: `local:${chave}`, valor });
}

// ---------------------------------------------------------------------
// Vínculos (contas de que o usuário participa, com a empresa de cada uma)
// ---------------------------------------------------------------------
export async function lerVinculos() {
  return bancoAberto().getAll('vinculos');
}

// Guarda os vínculos que vieram do servidor. A empresa com alteração ainda na
// fila fica com essa alteração por cima. Os dados das empresas que a pessoa
// não usa mais (saiu ou perdeu o acesso) saem do aparelho.
export async function guardarVinculos(vinculos) {
  const db = bancoAberto();
  const tx = db.transaction(['vinculos', 'fila'], 'readwrite');
  const pendentes = (await tx.objectStore('fila').getAll()).filter((item) => item.tabela === 'empresas');
  await tx.objectStore('vinculos').clear();
  const guardados = [];
  for (const v of vinculos) {
    let empresa = v.empresa;
    for (const item of pendentes) if (item.registro_id === empresa.id) empresa = { ...empresa, ...item.dados };
    const registro = { empresa_id: empresa.id, papel: v.papel, colaborador_id: v.colaborador_id ?? null, empresa };
    await tx.objectStore('vinculos').put(registro);
    guardados.push(registro);
  }
  await tx.done;
  await esquecerEmpresas(new Set(guardados.map((v) => v.empresa_id)));
  return guardados;
}

// Apaga do aparelho tudo das empresas que não estão em `ficam`: as linhas das
// tabelas, o que estava na fila, as fotos e as marcas de baixa (se a pessoa
// voltar a ter acesso, a empresa é baixada de novo). As categorias do sistema
// (sem empresa) ficam. Devolve os ids das empresas apagadas.
//
// Sem cursores no IndexedDB: o Safari do iPhone (e todo navegador do iPhone,
// que usa o mesmo motor) falha ao abrir alguns, como o de chaves únicas num
// índice vazio ("UnknownError: Unable to open cursor"; uma conta nova no
// iPhone não conseguia entrar, em 08/10/2026). Só getAll, getAllKeys, count,
// get e delete.
export async function esquecerEmpresas(ficam) {
  const db = bancoAberto();
  const tx = db.transaction([...TABELAS, 'fila', 'arquivos', 'meta'], 'readwrite');
  const meta = tx.objectStore('meta');
  const fila = tx.objectStore('fila');
  // Empresas que podem ter dados aqui: as das marcas de baixa e das contas
  // fixas (a empresa pode não ter nenhuma linha) e as da fila.
  const conhecidas = new Set();
  const marcas = [];
  for (const chave of await meta.getAllKeys()) {
    const achou = /^(baixado|fixas):(.+)$/.exec(String(chave));
    if (!achou) continue;
    conhecidas.add(achou[2]);
    marcas.push([chave, achou[2]]);
  }
  const itensDaFila = await fila.getAll();
  for (const item of itensDaFila) if (item.empresa_id) conhecidas.add(item.empresa_id);
  // Nas tabelas, pela contagem do índice: se o total passa da soma das
  // empresas conhecidas, há linhas de outra empresa (raro) e a tabela é lida.
  for (const tabela of TABELAS) {
    const indice = tx.objectStore(tabela).index('empresa_id');
    const total = await indice.count();
    if (total === 0) continue;
    let somadas = 0;
    for (const id of new Set([...conhecidas, ...ficam])) somadas += await indice.count(IDBKeyRange.only(id));
    if (somadas >= total) continue;
    for (const linha of await tx.objectStore(tabela).getAll()) if (linha.empresa_id != null) conhecidas.add(linha.empresa_id);
  }
  const saem = new Set([...conhecidas].filter((id) => !ficam.has(id)));
  if (saem.size > 0) {
    for (const tabela of TABELAS) {
      const loja = tx.objectStore(tabela);
      for (const empresaId of saem) {
        for (const chave of await loja.index('empresa_id').getAllKeys(IDBKeyRange.only(empresaId))) await loja.delete(chave);
      }
    }
    for (const item of itensDaFila) if (saem.has(item.empresa_id)) await fila.delete(item.seq);
    // As fotos, uma de cada vez (podem ser grandes).
    const arquivos = tx.objectStore('arquivos');
    for (const chave of await arquivos.getAllKeys()) {
      if (saem.has((await arquivos.get(chave))?.empresa_id)) await arquivos.delete(chave);
    }
    for (const [chave, empresaId] of marcas) if (saem.has(empresaId)) await meta.delete(chave);
  }
  await tx.done;
  return [...saem];
}
